import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Global polling hook for channel unread counts.
 * - Polls every 10s while tab is visible
 * - Pauses when tab hidden, refreshes immediately on focus
 * - Provides total + per-channel map
 * - Exposes `markChannelRead(channelId)` to optimistically clear local count
 */
export const useUnreadMessages = (pollMs = 10000) => {
  const { user, api } = useAuth();
  const [counts, setCounts] = useState({});
  const [dmCounts, setDmCounts] = useState({});
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchCounts = useCallback(async () => {
    if (!user) return;
    const [channels, dms] = await Promise.allSettled([
      api.get('/channels-unread'),
      api.get('/dm/unread-counts'),
    ]);
    if (!mountedRef.current) return;
    if (channels.status === 'fulfilled') setCounts(channels.value.data || {});
    if (dms.status === 'fulfilled') setDmCounts(dms.value.data?.counts || {});
  }, [user, api]);

  const markChannelRead = useCallback((channelId) => {
    setCounts(prev => {
      if (!prev[channelId]) return prev;
      const copy = { ...prev };
      delete copy[channelId];
      return copy;
    });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!user) return undefined;

    // Initial fetch
    fetchCounts();

    // Setup polling — pause while tab hidden, restart on focus
    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(fetchCounts, pollMs);
    };
    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchCounts(); // refresh immediately on focus
        startPolling();
      }
    };

    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', fetchCounts);

    return () => {
      mountedRef.current = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', fetchCounts);
    };
  }, [user, fetchCounts, pollMs]);

  const total = Object.values(counts).reduce((sum, v) => sum + (v || 0), 0);
  const dmTotal = Object.values(dmCounts).reduce((sum, v) => sum + (v || 0), 0);

  return { counts, total, dmCounts, dmTotal, refresh: fetchCounts, markChannelRead };
};

export default useUnreadMessages;
